import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { AddressBlock } from "@shared-kernel/primary/shared/address-autocomplete/address-block";
import { EUROPEAN_COUNTRIES } from "@shared-kernel/domain/types/supported-country";
import { Checkbox } from "@shared-kernel/primary/shared/shadcn/ui/checkbox";
import { FormControl, FormField, FormItem, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Label } from "@shared-kernel/primary/shared/shadcn/ui/label";
import { LongTextField } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { STUDIO_EQUIPMENT_DESCRIPTION_PLACEHOLDER } from "@academy-context/primary/shared/studio-detail/studio-equipment-field";
import { StudioEnvironmentFields } from "@academy-context/primary/shared/studio-environment/studio-environment-fields.content";
import type { TeacherOnboardingFormInputs } from "../../form-validation/full-form.schema";

interface StudioDetailsFormProps {
  nameFieldProps?: { disabled?: boolean };
  priceFieldProps?: { disabled?: boolean };
}

export const StudioDetailsForm = ({ nameFieldProps = {}, priceFieldProps = {} }: StudioDetailsFormProps) => {
  const { control, watch, setValue } = useFormContext<TeacherOnboardingFormInputs>();
  const [useAdminAddress, setUseAdminAddress] = useState(false);

  const handleUseAdminAddressChange = (checked: boolean) => {
    setUseAdminAddress(checked);
    if (checked) {
      const postalAddress = watch("postalAddress");
      const addressComplement = watch("addressComplement");
      const country = watch("country");
      setValue("studio.details.postalAddress", postalAddress, { shouldValidate: true, shouldDirty: true });
      setValue("studio.details.addressComplement", addressComplement, { shouldValidate: true, shouldDirty: true });
      setValue("studio.details.country", country, { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <div className="space-y-4">
      <div className="col-span-1 md:col-span-2">
        <h3 className="text-base font-semibold">Informations générales</h3>
      </div>
      <FormField
        name="studio.details.name"
        render={({ field }) => (
          <FormItem>
            <Label>Nom du studio {!nameFieldProps.disabled && "*"}</Label>
            <FormControl>
              <Input {...field} {...nameFieldProps} placeholder="Nom du studio" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-row items-center space-x-2">
        <Checkbox checked={useAdminAddress} onCheckedChange={handleUseAdminAddressChange} id="use-admin-address" />
        <Label htmlFor="use-admin-address" className="cursor-pointer text-sm font-normal">
          Utiliser l&apos;adresse administrative
        </Label>
      </div>

      <AddressBlock
        fields={{
          postalAddress: "studio.details.postalAddress",
          addressComplement: "studio.details.addressComplement",
          country: "studio.details.country",
        }}
        countries={EUROPEAN_COUNTRIES}
        disabled={useAdminAddress}
        addressSubtitle="Veillez à saisir une adresse précise : elle sera utilisée dans les convocations envoyées aux élèves."
      />

      <LongTextField
        control={control}
        name="studio.details.equipmentDescription"
        label="Description des moyens techniques du studio *"
        placeholder={STUDIO_EQUIPMENT_DESCRIPTION_PLACEHOLDER}
      />

      <FormField
        name="studio.details.price"
        render={({ field }) => (
          <FormItem>
            <Label>Tarif horaire (€/h) {!priceFieldProps.disabled && "*"}</Label>
            <FormControl>
              <Input {...field} type="number" value={field.value ?? ""} disabled={priceFieldProps.disabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="studio.details.isMobilityAccessible"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <Label>Accessible PMR</Label>
              <p className="text-sm text-muted-foreground">Le studio est accessible aux personnes à mobilité réduite</p>
            </div>
          </FormItem>
        )}
      />

      <StudioEnvironmentFields namePrefix="studio.details.environment" />
    </div>
  );
};
